import React, { useState, useEffect } from 'react';
import { Search, Calendar, Plus, X, Loader2, Edit3, Trash2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

export default function AdmissionImportantDates() {
  const [dataList, setDataList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Modal Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Bulk / Single Form State
  const [academicYear, setAcademicYear] = useState('AY 2026–27');
  const [sectionHeading, setSectionHeading] = useState('Important Dates – AY 2026–27');
  const [datesList, setDatesList] = useState([
    { event_title: 'Admission Notification', event_date: 'July 1, 2026', badge_note: '', is_highlighted: false },
    { event_title: 'Online Application Starts', event_date: 'July 5, 2026', badge_note: '', is_highlighted: false },
    { event_title: 'Last Date for Submission (Without Fine)', event_date: 'September 30, 2026', badge_note: 'IMPORTANT DEADLINE', is_highlighted: true }
  ]);

  // Single Edit Item State
  const [singleForm, setSingleForm] = useState({
    academic_year: 'AY 2026–27',
    section_heading: 'Important Dates – AY 2026–27',
    event_title: '',
    event_date: '',
    badge_note: '',
    is_highlighted: false,
    display_order: 1
  });

  useEffect(() => {
    fetchDates();
  }, [searchTerm]);

  const fetchDates = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/admission-dates?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setDataList(result.data || []);
      else setDataList([]);
    } catch (err) {
      console.error("Failed to load dates:", err);
      setDataList([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setAcademicYear('AY 2026–27');
    setSectionHeading('Important Dates – AY 2026–27');
    setDatesList([
      { event_title: '', event_date: '', badge_note: '', is_highlighted: false }
    ]);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setSingleForm({
      academic_year: item.academic_year || 'AY 2026–27',
      section_heading: item.section_heading || 'Important Dates – AY 2026–27',
      event_title: item.event_title || '',
      event_date: item.event_date || '',
      badge_note: item.badge_note || '',
      is_highlighted: item.is_highlighted === 1 || item.is_highlighted === true,
      display_order: item.display_order || 1
    });
    setIsModalOpen(true);
  };

  // Dynamic Row Handlers for Add Mode
  const handleAddRow = () => {
    setDatesList(prev => [...prev, { event_title: '', event_date: '', badge_note: '', is_highlighted: false }]);
  };

  const handleRemoveRow = (index) => {
    setDatesList(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleRowChange = (index, field, value) => {
    setDatesList(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        // PUT Single Update
        const res = await fetch(`https://metapi.emedhatechnologies.com/api/admission-dates/update-entry/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(singleForm)
        });
        const result = await res.json();
        if (result.success) {
          setIsModalOpen(false);
          fetchDates();
        } else alert(result.message || "Failed to update.");
      } else {
        // POST Bulk Creation
        const payload = {
          academic_year: academicYear,
          section_heading: sectionHeading,
          dates_list: datesList.filter(d => d.event_title.trim() && d.event_date.trim())
        };

        if (payload.dates_list.length === 0) {
          alert("Please fill at least one date event title and date.");
          return;
        }

        const res = await fetch('https://metapi.emedhatechnologies.com/api/admission-dates/add-entry', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await res.json();
        if (result.success) {
          setIsModalOpen(false);
          fetchDates();
        } else alert(result.message || "Failed to add.");
      }
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this admission date?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/admission-dates/delete-entry/${id}`, {
        method: 'PUT'
      });
      const result = await res.json();
      if (result.success) fetchDates();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  // Pagination Computations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER ACTION BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Admission Important Dates & Milestones</h2>
          <p style={styles.mainSubtitle}>Publish academic schedules, application deadlines, fee penalty brackets, and exam dates</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Add Admission Dates
        </button>
      </div>

      {/* FILTER PANEL */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by event title, date, or academic year..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Schedule Entries: <strong style={{ color: '#1c6ec9' }}>{dataList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={{ ...styles.tableTh, width: '120px' }}>Academic Year</th>
                <th style={styles.tableTh}>Event Milestone</th>
                <th style={styles.tableTh}>Scheduled Date / Period</th>
                <th style={styles.tableTh}>Status / Highlighting</th>
                <th style={{ ...styles.tableTh, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr><td colSpan="5" style={styles.noDataTd}>No admission dates logged yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={item.is_highlighted ? styles.tableTrHighlighted : styles.tableTr}>
                    <td style={styles.tableTd}>
                      <span style={styles.academicYearBadge}>{item.academic_year}</span>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={item.is_highlighted ? styles.calendarIconBoxRed : styles.calendarIconBox}>
                          <Calendar size={14} color={item.is_highlighted ? '#dc2626' : '#2563eb'} />
                        </div>
                        <div>
                          <strong style={{ color: item.is_highlighted ? '#991b1b' : '#0f172a', fontSize: '13.5px' }}>
                            {item.event_title}
                          </strong>
                          {item.badge_note ? (
                            <div style={styles.badgeNoteText}>{item.badge_note}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <span style={{ fontWeight: '700', color: item.is_highlighted ? '#991b1b' : '#334155', fontSize: '13px' }}>
                        {item.event_date}
                      </span>
                    </td>
                    <td style={styles.tableTd}>
                      {item.is_highlighted ? (
                        <span style={styles.urgentBadge}>
                          <AlertTriangle size={12} style={{ marginRight: '4px' }} /> Urgent / Deadline
                        </span>
                      ) : (
                        <span style={styles.normalBadge}>Standard Milestone</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Date"><Edit3 size={12} /></button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Date"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {dataList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* MODAL POPUP (SUPPORTS BOTH BULK ADD AND SINGLE EDIT) */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modalWrapperCard, maxWidth: editingId ? '520px' : '720px' }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} color="#1c6ec9" />
                {editingId ? 'Modify Admission Date Milestone' : 'Add Admission Important Dates Schedule'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* EDIT MODE (SINGLE ITEM) */}
              {editingId ? (
                <>
                  <div style={styles.formSplitGrid}>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Academic Year *</label>
                      <input
                        type="text"
                        required
                        value={singleForm.academic_year}
                        onChange={(e) => setSingleForm({ ...singleForm, academic_year: e.target.value })}
                        style={styles.formInput}
                      />
                    </div>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Header / Section Title</label>
                      <input
                        type="text"
                        value={singleForm.section_heading}
                        onChange={(e) => setSingleForm({ ...singleForm, section_heading: e.target.value })}
                        style={styles.formInput}
                      />
                    </div>
                  </div>

                  <div style={styles.fieldContainer}>
                    <label style={styles.fieldLabel}>Event Title *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Last Date for Submission (Without Fine)"
                      value={singleForm.event_title}
                      onChange={(e) => setSingleForm({ ...singleForm, event_title: e.target.value })}
                      style={styles.formInput}
                    />
                  </div>

                  <div style={styles.formSplitGrid}>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Event Date / Period *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. September 30, 2026 or April / May 2026"
                        value={singleForm.event_date}
                        onChange={(e) => setSingleForm({ ...singleForm, event_date: e.target.value })}
                        style={styles.formInput}
                      />
                    </div>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Badge Note (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. IMPORTANT DEADLINE"
                        value={singleForm.badge_note}
                        onChange={(e) => setSingleForm({ ...singleForm, badge_note: e.target.value })}
                        style={styles.formInput}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#0f172a', fontWeight: '600' }}>
                      <input
                        type="checkbox"
                        checked={singleForm.is_highlighted}
                        onChange={(e) => setSingleForm({ ...singleForm, is_highlighted: e.target.checked })}
                      />
                      Highlight as Urgent Deadline (Red Border & Background)
                    </label>
                  </div>
                </>
              ) : (
                /* ADD MODE (MULTI-ROW BUILDER) */
                <>
                  <div style={styles.formSplitGrid}>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Academic Year *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. AY 2026–27"
                        value={academicYear}
                        onChange={(e) => {
                          setAcademicYear(e.target.value);
                          setSectionHeading(`Important Dates – ${e.target.value}`);
                        }}
                        style={styles.formInput}
                      />
                    </div>
                    <div style={styles.fieldContainer}>
                      <label style={styles.fieldLabel}>Section Heading</label>
                      <input
                        type="text"
                        value={sectionHeading}
                        onChange={(e) => setSectionHeading(e.target.value)}
                        style={styles.formInput}
                      />
                    </div>
                  </div>

                  {/* DYNAMIC MULTI-ROW LIST */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                    <label style={{ ...styles.fieldLabel, color: '#1c6ec9' }}>Admission Date Milestones ({datesList.length} Rows)</label>
                    <button type="button" onClick={handleAddRow} style={styles.addRowBtn}>
                      <Plus size={13} /> Add Another Milestone
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '35vh', overflowY: 'auto', paddingRight: '4px' }}>
                    {datesList.map((row, idx) => (
                      <div key={idx} style={row.is_highlighted ? styles.rowCardHighlighted : styles.rowCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>Milestone #{idx + 1}</span>
                          {datesList.length > 1 && (
                            <button type="button" onClick={() => handleRemoveRow(idx)} style={styles.rowRemoveBtn}>
                              <X size={14} /> Remove
                            </button>
                          )}
                        </div>

                        <div style={styles.formSplitGrid}>
                          <input
                            type="text"
                            required
                            placeholder="Milestone Title (e.g. Online Application Starts)"
                            value={row.event_title}
                            onChange={(e) => handleRowChange(idx, 'event_title', e.target.value)}
                            style={styles.formInput}
                          />
                          <input
                            type="text"
                            required
                            placeholder="Date / Period (e.g. July 5, 2026)"
                            value={row.event_date}
                            onChange={(e) => handleRowChange(idx, 'event_date', e.target.value)}
                            style={styles.formInput}
                          />
                        </div>

                        <div style={{ ...styles.formSplitGrid, marginTop: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Badge Note (e.g. IMPORTANT DEADLINE)"
                            value={row.badge_note}
                            onChange={(e) => handleRowChange(idx, 'badge_note', e.target.value)}
                            style={{ ...styles.formInput, fontSize: '12px' }}
                          />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#991b1b', fontWeight: '600', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={row.is_highlighted}
                              onChange={(e) => handleRowChange(idx, 'is_highlighted', e.target.checked)}
                            />
                            Highlight (Red Box)
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>
                  {editingId ? 'Save Changes' : 'Publish All Dates'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '380px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTrHighlighted: { borderBottom: '1px solid #fecaca', background: '#fff5f5' },
  tableTd: { padding: '14px 14px', color: '#475569', fontSize: '13.5px', verticalAlign: 'middle' },
  noDataTd: { padding: '36px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  calendarIconBox: { width: '28px', height: '28px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  calendarIconBoxRed: { width: '28px', height: '28px', borderRadius: '6px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  badgeNoteText: { fontSize: '10.5px', color: '#b91c1c', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '2px' },
  academicYearBadge: { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700' },
  urgentBadge: { display: 'inline-flex', alignItems: 'center', background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700' },
  normalBadge: { display: 'inline-block', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600' },
  actionBtnGroup: { display: 'flex', gap: '6px' },
  editRowBtn: { background: '#ffffff', border: '1px solid #cbd5e1', color: '#475569', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  deleteRowBtn: { background: '#ffffff', border: '1px solid #fecaca', color: '#b91c1c', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxHeight: '88vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px', overflowY: 'auto' },
  formSplitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  addRowBtn: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '4px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  rowCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' },
  rowCardHighlighted: { background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px' },
  rowRemoveBtn: { background: 'transparent', border: 'none', color: '#dc2626', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '2px' },
  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }
};